import type { ComponentProps } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Appearance,
  ActivityIndicator,
  AppState,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  useColorScheme,
  View,
} from "react-native";

import { Modal } from "@/components/ui/app-modal";
import Ionicons from "@expo/vector-icons/Ionicons";
import {
  useFocusEffect,
  useRouter,
  type ErrorBoundaryProps,
} from "expo-router";
import { StatusBar } from "expo-status-bar";
import { SafeAreaView } from "react-native-safe-area-context";

import { Logo } from "@/components/brand/logo";
import { StayVisibleDiagnosticsRow } from "@/components/stay-visible-diagnostics-row";
import { ProfileVibeCardEntry } from "@/components/profile/profile-vibe-card-entry";
import { Avatar } from "@/components/ui/avatar";
import { BlurredBackdrop } from "@/components/ui/blurred-backdrop";
import { ApiError } from "@/api/client";
import { hasAvatarBadge } from "@/lib/avatar-badge";
import { notify } from "@/lib/dialog";
import { useTrialOffer } from "@/lib/trial-offer";
import type { TranslationKey } from "@/i18n";
import { prefetchDashboard } from "@/lib/dashboard-prefetch";
import { openAppSettings } from "@/lib/location-access";
import {
  getNotificationPermission,
  registerForPushNotifications,
  type NotificationPermissionState,
} from "@/lib/notifications";
import {
  friendshipGoalLabel,
  interestLabel,
  languageLabel,
  politicsLabel,
  profileListText,
  religionLabel,
} from "@/lib/profile-labels";
import { useAuthStore } from "@/state/auth-store";
import { useAppTheme } from "@/theme/app-theme";
import { useSessionStore } from "@/state/session-store";
import { useUpgradeStore } from "@/state/upgrade-store";
import { useStayVisibleStore } from "@/state/stay-visible-store";

import type { NotificationPreferences } from "@/types";

const FONT_FAMILY = Platform.select({
  ios: "Avenir Next",
  android: "sans-serif",
  default: undefined,
});

type IoniconName = ComponentProps<typeof Ionicons>["name"];

type ConfirmationType =
  | "signOut"
  | "deactivateAccount"
  | "reactivateAccount"
  | "deleteAccount"
  | null;

type ThemePreference = "device" | "light" | "dark";

type UserWithDetails = {
  about?: string;
  age?: number | string;
  ageBand?: string | null;
  religion?: string;
  politics?: string;
  gender?: string | null;
  interests?: string[];
  lookingFor?: string[] | null;
  languages?: string[] | null;
  nearbyRadiusM?: number | null;
  visibilityPreferences?: {
    ageMin?: number | null;
    ageMax?: number | null;
    genders?: string[] | null;
  } | null;
  revealDetails?: boolean;
};

function formatPlanDate(iso?: string | null): string {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

const DEFAULT_NOTIFICATION_PREFERENCES: NotificationPreferences = {
  muteAll: false,
  newPeople: true,
  cues: true,
  messages: true,
  goalReminders: true,
};

type ThemeOption = {
  labelKey: TranslationKey;
  value: ThemePreference;
  icon: IoniconName;
};

const THEME_OPTIONS: ThemeOption[] = [
  {
    labelKey: "profile.appearance.device",
    value: "device",
    icon: "phone-portrait-outline",
  },
  {
    labelKey: "profile.appearance.light",
    value: "light",
    icon: "sunny-outline",
  },
  {
    labelKey: "profile.appearance.dark",
    value: "dark",
    icon: "moon-outline",
  },
];

const GENDER_DISPLAY: Record<string, TranslationKey> = {
  woman: "options.gender.woman",
  man: "options.gender.man",
  "non-binary": "options.gender.nonBinary",
  "another-identity": "options.gender.other",
  "prefer-not-to-say": "options.gender.preferNotToSay",
};

function getThemeColors(isDark: boolean) {
  return {
    screen: isDark ? "#0D1724" : "#FCFDFF",
    topBar: isDark ? "#101C2C" : "#FCFDFF",

    surface: isDark ? "#152235" : "#FFFFFF",
    surfaceMuted: isDark ? "#19283B" : "#FAFBFD",
    surfaceSoft: isDark ? "#1B2C43" : "#F4F8FF",
    surfacePressed: isDark ? "#213650" : "#F4F8FF",

    textPrimary: isDark ? "#EFF5FF" : "#102D51",
    textStrong: isDark ? "#DFEAFA" : "#153252",
    textBody: isDark ? "#C0CDDD" : "#425873",
    textSecondary: isDark ? "#A8B6C9" : "#7B879A",
    textMuted: isDark ? "#8999AD" : "#929DAC",

    border: isDark ? "#2C3C51" : "#E1E8F1",
    borderSoft: isDark ? "#29394D" : "#E4EAF2",
    divider: isDark ? "#2A3A4E" : "#E3E9F1",

    primary: isDark ? "#6EA8FF" : "#2468D8",
    primaryStrong: isDark ? "#91BCFF" : "#2463CB",
    primaryText: isDark ? "#B9D3FF" : "#3568B7",
    primarySoft: isDark ? "#203754" : "#F0F5FD",
    primarySurface: isDark ? "#1D314B" : "#F3F7FF",
    primaryBorder: isDark ? "#34577F" : "#DDE7F7",

    iconNeutral: isDark ? "#A9B9CD" : "#526C8D",
    hidden: isDark ? "#7F8FA3" : "#8996A8",

    green: isDark ? "#57C98D" : "#35A86C",

    danger: isDark ? "#FF929B" : "#A64A54",
    dangerStrong: isDark ? "#FF7D88" : "#B4232F",
    dangerSoft: isDark ? "#3A222B" : "#FFF3F4",
    dangerSoftStrong: isDark ? "#42232D" : "#FFF0F1",
    dangerBorder: isDark ? "#693943" : "#F2CDD1",

    modalBackdrop: isDark ? "rgba(1, 7, 14, 0.72)" : "rgba(12, 29, 50, 0.44)",

    shadow: isDark ? "#000000" : "#7187A6",
    white: "#FFFFFF",
  };
}

type ThemeColors = ReturnType<typeof getThemeColors>;

function applyAppearancePreference(preference: ThemePreference) {
  type CompatibleColorScheme = "light" | "dark" | "unspecified" | null;

  if (typeof Appearance.setColorScheme !== "function") {
    return;
  }

  const setColorScheme = Appearance.setColorScheme as unknown as (
    scheme: CompatibleColorScheme,
  ) => void;

  if (preference === "device") {
    try {
      setColorScheme("unspecified");
    } catch {
      try {
        setColorScheme(null);
      } catch {
        // Clearing the override is unsupported
        // on the current platform.
      }
    }

    return;
  }

  try {
    setColorScheme(preference);
  } catch {
    // The profile still applies the selected
    // theme locally if this API is unavailable.
  }
}

export function ErrorBoundary({ error, retry }: ErrorBoundaryProps) {
  const { t } = useTranslation();
  const systemColorScheme = useColorScheme();
  const isDark = systemColorScheme === "dark";
  const colors = useMemo(() => getThemeColors(isDark), [isDark]);
  const styles = useMemo(() => createStyles(colors, isDark), [colors, isDark]);
  return (
    <SafeAreaView edges={["top", "bottom"]} style={styles.safe}>
      <StatusBar style={isDark ? "light" : "dark"} />

      <View style={styles.errorFallback}>
        <View style={styles.errorIcon}>
          <Ionicons
            name="alert-circle-outline"
            size={24}
            color={colors.primary}
          />
        </View>

        <Text style={styles.errorTitle}>{t("profile.screen.errorTitle")}</Text>

        <Text style={styles.errorText}>{t("profile.screen.errorBody")}</Text>

        {__DEV__ && error.message ? (
          <Text numberOfLines={3} style={styles.errorDetails}>
            {error.message}
          </Text>
        ) : null}

        <Pressable
          accessibilityRole="button"
          accessibilityLabel={t("profile.screen.retryA11y")}
          onPress={retry}
          style={({ pressed }) => [
            styles.modalButton,
            styles.planConfirmButton,
            styles.errorRetryButton,
            pressed && styles.modalButtonPressed,
          ]}
        >
          <Ionicons name="refresh" size={18} color={colors.white} />

          <Text style={styles.confirmButtonText}>{t("common.tryAgain")}</Text>
        </Pressable>
      </View>
    </SafeAreaView>
  );
}

export default function Profile() {
  const router = useRouter();
  const { t } = useTranslation();
  const systemColorScheme = useColorScheme();

  const user = useAuthStore((state) => state.user);

  const signOut = useAuthStore((state) => state.signOut);

  const deleteAccount = useAuthStore((state) => state.deleteAccount);

  const setAccountDeactivated = useAuthStore(
    (state) => state.setAccountDeactivated,
  );

  /** The account is switched off socially. Read from the server's own record. */
  const deactivated = Boolean(user?.deactivatedAt);
  const setProfile = useAuthStore((state) => state.setProfile);

  const setDevPlan = useAuthStore((state) => state.setDevPlan);

  const forceStayVisiblePrompt = useStayVisibleStore(
    (state) => state.forcePrompt,
  );

  const currentVenueId = useSessionStore((state) => state.venue?.id ?? null);

  // From the theme provider, which owns this. It used to be read off the auth
  // store through a cast — a field that store neither declared nor persisted,
  // so the choice was lost on every reload. See `lib/theme-preference-io.ts`.
  const { preference: storedThemePreference, setPreference } = useAppTheme();

  // Derived, not mirrored. This was local state kept in sync by an effect, but
  // the provider is the source of truth and `onChangeTheme` already writes it —
  // so the copy only added a render round-trip and a way for the two to
  // disagree. The context re-renders this screen when the value changes, which
  // is what the effect was imitating.
  const themePreference = storedThemePreference;

  const resolvedTheme =
    themePreference === "device"
      ? (systemColorScheme ?? "light")
      : themePreference;

  const isDark = resolvedTheme === "dark";

  const colors = useMemo(() => getThemeColors(isDark), [isDark]);

  const styles = useMemo(() => createStyles(colors, isDark), [colors, isDark]);
  const details = user as
    (NonNullable<typeof user> & UserWithDetails) | null | undefined;

  const [confirmationType, setConfirmationType] =
    useState<ConfirmationType>(null);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [changingDeactivation, setChangingDeactivation] = useState(false);

  const [notificationSettingsOpen, setNotificationSettingsOpen] =
    useState(false);

  // The visible/private profile blocks collapse into dropdowns so the screen
  // opens compact; tapping a header reveals its detail rows.
  const [visibleProfileOpen, setVisibleProfileOpen] = useState(false);
  const [privateProfileOpen, setPrivateProfileOpen] = useState(false);

  // When an "Upgrade to Plus" affordance elsewhere routed here, send the user
  // to the dedicated pricing page.
  const upgradeRequested = useUpgradeStore((s) => s.requested);
  const consumeUpgrade = useUpgradeStore((s) => s.consume);
  useFocusEffect(
    useCallback(() => {
      if (upgradeRequested) {
        consumeUpgrade();
        router.push("/pricing");
      }
    }, [upgradeRequested, consumeUpgrade, router]),
  );

  const [savingNotifications, setSavingNotifications] = useState(false);

  // The OS permission behind every notification toggle. Read when the sheet
  // opens and re-read on each foreground while it is open, because the OS
  // never tells the app about a change made in its settings — you have to
  // look again on the way back.
  const [notificationPermission, setNotificationPermission] =
    useState<NotificationPermissionState | null>(null);
  useEffect(() => {
    if (!notificationSettingsOpen) return undefined;
    let active = true;
    const read = () =>
      void getNotificationPermission().then((state) => {
        if (active) setNotificationPermission(state);
      });
    read();
    const sub = AppState.addEventListener("change", (state) => {
      if (state === "active") read();
    });
    return () => {
      active = false;
      sub.remove();
    };
  }, [notificationSettingsOpen]);

  /**
   * The recovery path for switches that read "off" because the OS permission
   * is missing: re-ask while asking still works; after that the OS prompt is
   * spent (iOS immediately, Android after the second refusal) and the app's
   * system settings page is the only place the answer can change, so go there.
   * The foreground listener above reads the verdict on the way back.
   */
  async function enableSystemNotifications() {
    const current = await getNotificationPermission();
    if (current.status === "granted") {
      setNotificationPermission(current);
      return;
    }
    if (!current.canAskAgain) {
      await openAppSettings();
      return;
    }
    await registerForPushNotifications();
    setNotificationPermission(await getNotificationPermission());
  }

  // Tab screens stay mounted when you switch away, so the ScrollView keeps its
  // offset. Reset to the top each time Profile regains focus so returning to
  // the tab starts fresh instead of resuming mid-scroll.
  const scrollRef = useRef<ScrollView>(null);
  useFocusEffect(
    useCallback(() => {
      scrollRef.current?.scrollTo({ y: 0, animated: false });
    }, []),
  );

  useEffect(() => {
    applyAppearancePreference(themePreference);
  }, [themePreference]);

  const about = details?.about?.trim();
  const headline = user?.headline?.trim();
  const displayName = user?.displayName?.trim();
  // Already the EFFECTIVE plan: `presentUser` sends `effectivePlan(user)`, so
  // a referral-granted Plus arrives here as `plan: "plus"`. The resolution is
  // the server's, deliberately — one definition of who has Plus, rather than
  // every client re-deriving it from a grant expiry. This drives the TIER:
  // the badge, the icon, the label.
  const currentPlan = user?.plan === "plus" ? "plus" : "free";

  // Whether there is a purchase to manage — a different question from the
  // tier, and only `plusSource` can answer it. Someone on a grant has every
  // Plus feature and no subscription, so they should see active access rather
  // than a fake manage/cancel path or an upgrade pitch before the grant ends.
  const hasSubscription = user?.plusSource === "subscription";
  const hasGrant = user?.plusSource === "grant";
  const grantUntil = hasGrant ? formatPlanDate(user?.plusGrantUntil) : "";
  // Free users get the trial pitch on the plan row when they're eligible —
  // the trial itself only ever starts from the checkout they land on.
  const trialOffer = useTrialOffer("plus_paywall_opened");
  const planLabel = t(
    currentPlan === "plus" ? "profile.plan.plusTier" : "profile.plan.free",
  );

  const age =
    details?.age !== undefined && details.age !== null
      ? String(details.age)
      : undefined;

  const ageBand = details?.ageBand?.trim();
  const religion = details?.religion
    ? religionLabel(details.religion)
    : undefined;
  const politics = details?.politics
    ? politicsLabel(details.politics)
    : undefined;
  const interests =
    profileListText(details?.interests, interestLabel, { separator: ", " }) ||
    undefined;
  const lookingFor =
    profileListText(details?.lookingFor, friendshipGoalLabel, {
      separator: ", ",
    }) || undefined;
  const languages =
    profileListText(details?.languages, languageLabel, { separator: ", " }) ||
    undefined;
  const gender = details?.gender
    ? GENDER_DISPLAY[details.gender]
      ? t(GENDER_DISPLAY[details.gender])
      : details.gender
    : undefined;

  const notificationPreferences: NotificationPreferences = {
    ...DEFAULT_NOTIFICATION_PREFERENCES,
    ...(user?.notificationPreferences ?? {}),
  };

  const notificationsEnabled =
    !notificationPreferences.muteAll &&
    (notificationPreferences.newPeople ||
      notificationPreferences.cues ||
      notificationPreferences.messages ||
      notificationPreferences.goalReminders);

  function openSignOutConfirmation() {
    setConfirmationType("signOut");
  }

  function openDeleteAccountConfirmation() {
    setConfirmationType("deleteAccount");
  }

  function openDeactivationConfirmation() {
    setConfirmationType(deactivated ? "reactivateAccount" : "deactivateAccount");
  }

  function closeConfirmation() {
    if (deletingAccount || changingDeactivation || signingOut) return;
    setConfirmationType(null);
  }

  /**
   * Deactivate, or come back. One handler because it is one button: the row
   * below reads its label, icon and copy from the same `deactivated` flag, so
   * there is no way for the button to say one thing and do the other.
   */
  async function handleDeactivationChange() {
    if (changingDeactivation) return;
    const next = !deactivated;
    setChangingDeactivation(true);
    try {
      await setAccountDeactivated(next);
      setConfirmationType(null);
      notify(
        t(next ? "profile.screen.deactivatedTitle" : "profile.screen.reactivatedTitle"),
        t(next ? "profile.screen.deactivatedBody" : "profile.screen.reactivatedBody"),
      );
    } catch (err) {
      setConfirmationType(null);
      notify(
        t("profile.screen.deactivateFailedTitle"),
        err instanceof ApiError || err instanceof Error
          ? err.message
          : t("profile.screen.deactivateFailedBody"),
      );
    } finally {
      setChangingDeactivation(false);
    }
  }

  async function handleSignOut() {
    if (signingOut) return;
    setSigningOut(true);
    try {
      // On success this clears the local session, flipping auth status to
      // 'unauthenticated' — the root guard routes back to welcome.
      await signOut();
      setConfirmationType(null);
    } catch {
      // Sign-out is all-or-nothing: the server did not confirm, so the session
      // is intact and the user is still signed in. Say so and let them retry.
      setConfirmationType(null);
      notify(t("errors.generic"), t("errors.tryAgainInAMoment"));
    } finally {
      setSigningOut(false);
    }
  }

  async function handleDeleteAccount() {
    if (deletingAccount) return;
    setDeletingAccount(true);
    try {
      // On success this clears the local session, which flips auth status to
      // 'unauthenticated' — the root routing guard then returns the user to the
      // welcome screen. The server also emails a deletion confirmation.
      await deleteAccount();
      setConfirmationType(null);
      notify(
        t("profile.screen.accountDeletedTitle"),
        t("profile.screen.accountDeletedBody"),
      );
    } catch (err) {
      setConfirmationType(null);
      notify(
        t("profile.screen.deleteFailedTitle"),
        err instanceof ApiError || err instanceof Error
          ? err.message
          : t("profile.screen.deleteFailedBody"),
      );
    } finally {
      setDeletingAccount(false);
    }
  }

  function onEditProfile() {
    router.push("/edit-profile");
  }

  async function updateNotificationPreferences(
    patch: Partial<NotificationPreferences>,
  ) {
    const next: NotificationPreferences = {
      ...notificationPreferences,
      ...patch,
    };

    if (
      ("newPeople" in patch ||
        "cues" in patch ||
        "messages" in patch ||
        "goalReminders" in patch) &&
      (patch.newPeople ||
        patch.cues ||
        patch.messages ||
        patch.goalReminders)
    ) {
      next.muteAll = false;
    }

    setSavingNotifications(true);
    // Every outcome below that speaks to the user does so through a dialog,
    // and this runs from inside the notification-settings Modal. iOS will not
    // present a dialog over an open Modal — it never appears, and its window
    // eats every touch. Each notice here is also terminal for this sheet:
    // "blocked" sends the person to OS settings, and the failures are not
    // fixed by flipping another switch, so closing first costs nothing.
    const closeSettings = () => setNotificationSettingsOpen(false);
    try {
      if (
        !next.muteAll &&
        (next.newPeople ||
          next.cues ||
          next.messages ||
          next.goalReminders)
      ) {
        const outcome = await registerForPushNotifications();
        if (outcome === "denied") {
          // The OS or browser refused. Settings are where this gets fixed.
          closeSettings();
          notify(
            t("profile.screen.notificationsBlockedTitle"),
            t("profile.screen.notificationsBlockedBody"),
          );
        } else if (outcome !== "registered") {
          // Permission is granted but nothing was stored, so no notification
          // can arrive. Deliberately NOT the "blocked" copy — it would tell the
          // user to fix device settings that are already correct. "unavailable"
          // is likely temporary, so it gets the try-again body; "rejected" will
          // not change on a retry.
          closeSettings();
          notify(
            t("profile.screen.notificationsFailedTitle"),
            outcome === "unavailable"
              ? t("errors.tryAgainInAMoment")
              : t("errors.generic"),
          );
        }
      }

      await setProfile({
        notificationPreferences: next,
      });
    } catch (err) {
      closeSettings();
      notify(
        t("profile.screen.notificationsFailedTitle"),
        err instanceof Error ? err.message : t("errors.tryAgainInAMoment"),
      );
    } finally {
      setSavingNotifications(false);
    }
  }

  function onChangeTheme(nextTheme: ThemePreference) {
    // One write, to the source of truth, which persists it. The context
    // re-renders this screen, which is what the removed local copy was doing a
    // second time.
    setPreference(nextTheme);
  }

  return (
    <SafeAreaView style={styles.safe} edges={["top"]}>
      <StatusBar style={isDark ? "light" : "dark"} />

      <View style={styles.topBar}>
        <View style={styles.topBarBrand}>
          <Logo size={24} />

          {/* The plan belongs to the account, not to the person, so it reads
              beside the wordmark rather than beside their name. Bare text, not
              a pill: a second bordered shape here competed with the wordmark
              instead of following it. */}
          <Text style={styles.planText}>{planLabel}</Text>
        </View>
      </View>

      <ScrollView
        ref={scrollRef}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={styles.content}
      >
        <View style={styles.card}>
          <View style={styles.profileGroup}>
            <View style={styles.profileSummary}>
              <View style={styles.avatarSection}>
                <Pressable
                  onPress={() => router.push("/edit-avatar")}
                  accessibilityRole="button"
                  accessibilityLabel={t("profile.avatar.editA11y")}
                  accessibilityHint={t("profile.avatar.editHint")}
                  style={styles.avatarBorder}
                >
                  <Avatar
                    seed={user?.avatarSeed ?? user?.displayName ?? "opncue"}
                    avatarConfig={user?.avatarConfig}
                    name={user?.displayName}
                    size={88}
                    emoji={user?.vibe?.emoji}
                    // The badge hangs onto the padded ring around the avatar,
                    // so its notch is cut in that colour, not the card's.
                    badgeRingColor={colors.surfaceSoft}
                  />
                </Pressable>

                {/* The pin/vibe badge lives in this corner when there is one,
                    so the presence dot yields to it instead of overlapping. */}
                {hasAvatarBadge(user?.avatarConfig) ? null : (
                  <View style={styles.onlineDot} />
                )}
              </View>

              <View style={styles.identity}>
                <Text numberOfLines={2} style={styles.displayName}>
                  {user?.displayName ?? t("profile.screen.you")}
                </Text>

                {headline ? (
                  <Text numberOfLines={2} style={styles.profileHeadline}>
                    {headline}
                  </Text>
                ) : null}

                {user?.vibe ? (
                  <View style={styles.vibeRow}>
                    <Text style={styles.vibeEmoji}>{user.vibe.emoji}</Text>

                    <Text style={styles.vibeText}>{user.vibe.label}</Text>
                  </View>
                ) : null}
              </View>
            </View>

            <View style={styles.divider} />

            {/* No verification entry, by the owner's rule (2026-09-03):
                users do not request age verification — Opncue requests it of
                specific users through a private, targeted flow when policy
                requires one. The dormant screen for that flow is
                components/verification/verify-age.tsx. */}
            <ProfileVibeCardEntry variant="button" />
          </View>

          <View style={styles.groupDivider} />

          <Text style={styles.cardTitle}>{t("profile.sections.profile")}</Text>

          <View style={styles.profileGroup}>
            <View style={styles.section}>
              <SectionHeader
                icon="document-text-outline"
                title={t("profile.sections.visibleProfile")}
                description={t("profile.sections.visibleProfileBody")}
                expanded={visibleProfileOpen}
                onToggle={() => setVisibleProfileOpen((open) => !open)}
                styles={styles}
                colors={colors}
              />

              {visibleProfileOpen ? (
                <View style={styles.detailsList}>
                  <DetailRow
                    icon="person-outline"
                    label={t("profile.sections.displayName")}
                    value={displayName}
                    styles={styles}
                    colors={colors}
                  />

                  <View style={styles.detailDivider} />

                  <DetailRow
                    icon="chatbubble-ellipses-outline"
                    label={t("profile.sections.headline")}
                    value={headline}
                    styles={styles}
                    colors={colors}
                  />

                  <View style={styles.detailDivider} />

                  <DetailRow
                    icon="calendar-outline"
                    label={t("profile.sections.ageBand")}
                    value={ageBand}
                    styles={styles}
                    colors={colors}
                  />

                  <View style={styles.detailDivider} />

                  <DetailRow
                    icon="people-outline"
                    label={t("profile.sections.friendshipGoal")}
                    value={lookingFor}
                    styles={styles}
                    colors={colors}
                    lines={3}
                  />

                  <View style={styles.detailDivider} />

                  {/* Moved here from the Private section 2026-08-20. Interests
                      were never private anywhere else — the nearby card, the
                      chat header and the guest profile's public half all show
                      them — so listing them under "only shared after reveal"
                      told the owner something false about who could see them. */}
                  <DetailRow
                    icon="sparkles-outline"
                    label={t("profile.sections.interests")}
                    value={interests}
                    styles={styles}
                    colors={colors}
                    lines={3}
                  />

                  <View style={styles.detailDivider} />

                  <DetailRow
                    icon="language-outline"
                    label={t("profile.sections.languages")}
                    value={languages}
                    styles={styles}
                    colors={colors}
                    lines={3}
                  />
                </View>
              ) : null}
            </View>

            <View style={styles.sectionDivider} />

            <View style={styles.section}>
              <SectionHeader
                icon="lock-closed-outline"
                title={t("profile.sections.privateProfile")}
                description={t("profile.sections.privateProfileBody")}
                expanded={privateProfileOpen}
                onToggle={() => setPrivateProfileOpen((open) => !open)}
                styles={styles}
                colors={colors}
              />

              {privateProfileOpen ? (
                <View style={styles.detailsList}>
                  <DetailRow
                    icon="calendar-number-outline"
                    label={t("profile.sections.actualAge")}
                    value={age}
                    styles={styles}
                    colors={colors}
                  />

                  <View style={styles.detailDivider} />

                  <DetailRow
                    icon="reader-outline"
                    label={t("profile.sections.about")}
                    value={about}
                    styles={styles}
                    colors={colors}
                    lines={4}
                  />

                  <View style={styles.detailDivider} />

                  <DetailRow
                    icon="person-circle-outline"
                    label={t("profile.sections.gender")}
                    value={gender}
                    styles={styles}
                    colors={colors}
                  />

                  <View style={styles.detailDivider} />

                  <DetailRow
                    icon="book-outline"
                    label={t("profile.sections.religion")}
                    value={religion}
                    styles={styles}
                    colors={colors}
                  />

                  <View style={styles.detailDivider} />

                  <DetailRow
                    icon="megaphone-outline"
                    label={t("profile.sections.politics")}
                    value={politics}
                    styles={styles}
                    colors={colors}
                  />
                </View>
              ) : null}
            </View>

            <View style={styles.sectionDivider} />

            <View style={styles.section}>
              <Pressable
                accessibilityRole="button"
                accessibilityLabel={t("profile.sections.edit")}
                onPress={onEditProfile}
                style={({ pressed }) => [
                  styles.sectionHeader,
                  pressed && styles.sectionHeaderPressed,
                ]}
              >
                <View style={styles.sectionIcon}>
                  <Ionicons
                    name="create-outline"
                    size={19}
                    color={colors.primary}
                  />
                </View>

                <View style={styles.sectionHeadingCopy}>
                  <Text style={styles.sectionTitle}>
                    {t("profile.sections.edit")}
                  </Text>

                  <Text style={styles.sectionDescription}>
                    {t("profile.sections.editBody")}
                  </Text>
                </View>

                <Ionicons
                  name="chevron-forward"
                  size={20}
                  color={colors.textMuted}
                />
              </Pressable>
            </View>
          </View>

          <View style={styles.groupDivider} />

          <Text style={styles.cardTitle}>{t("profile.sections.account")}</Text>

          <Pressable
            accessibilityRole="button"
            accessibilityLabel={t("profile.sections.progressA11y")}
            // Start the dashboard's reads on press-in, not on arrival: the
            // screen is a route, so it fetches from nothing when it mounts,
            // and this leads that by a tap plus a navigation animation. Bounded
            // inside `prefetchDashboard` — single-flight, skipped when the
            // answer is already cached or metrics are switched off, and joined
            // rather than duplicated by the screen it is for.
            onPressIn={() => prefetchDashboard(user?.id)}
            onPress={() => router.push("/dashboard")}
            style={({ pressed }) => [
              styles.accountRow,
              pressed && styles.accountRowPressed,
            ]}
          >
            <View style={styles.accountIcon}>
              <Ionicons
                name="trending-up-outline"
                size={20}
                color={colors.primary}
              />
            </View>

            <View style={styles.accountRowCopy}>
              <Text style={styles.accountRowTitle}>
                {t("profile.sections.progress")}
              </Text>

              <Text style={styles.accountRowDescription}>
                {t("profile.sections.progressBody")}
              </Text>
            </View>

            <Ionicons
              name="chevron-forward"
              size={20}
              color={colors.textMuted}
            />
          </Pressable>

          <View style={styles.accountDivider} />

          {/* Not on web, where background location does not exist. The row
              used to render everywhere and open a prompt the browser can never
              honour: tapping Continue reached `requestBackgroundLocation`,
              which answers "unavailable" on web, and the user was told
              "Not available in this build" — blaming the BUILD for something
              no browser build could ever do.

              The row and its trailing divider come out together so the
              section does not render two rules in a row.

              `forcePrompt` is guarded in the store as well. This hides the
              control; that keeps the rule true for any future caller. */}
          {Platform.OS !== "web" ? (
            <>
              <Pressable
                accessibilityRole="button"
                accessibilityLabel={t("profile.sections.stayVisibleA11y")}
                onPress={() => forceStayVisiblePrompt(currentVenueId)}
                style={({ pressed }) => [
                  styles.accountRow,
                  pressed && styles.accountRowPressed,
                ]}
              >
                <View style={styles.accountIcon}>
                  <Ionicons name="eye-outline" size={20} color={colors.primary} />
                </View>

                <View style={styles.accountRowCopy}>
                  <Text style={styles.accountRowTitle}>
                    {t("profile.sections.stayVisible")}
                  </Text>

                  <Text style={styles.accountRowDescription}>
                    {t("profile.sections.stayVisibleBody")}
                  </Text>
                </View>

                <Ionicons
                  name="chevron-forward"
                  size={20}
                  color={colors.textMuted}
                />
              </Pressable>

              <View style={styles.accountDivider} />
            </>
          ) : null}

          {hasGrant ? (
            <View style={styles.accountRow}>
              <View style={styles.accountIcon}>
                <Ionicons
                  name="sparkles-outline"
                  size={20}
                  color={colors.primary}
                />
              </View>

              <View style={styles.accountRowCopy}>
                <Text style={styles.accountRowTitle}>
                  {t("profile.sections.plusGrantActive")}
                </Text>

                <Text style={styles.accountRowDescription}>
                  {grantUntil
                    ? t("profile.sections.plusGrantActiveUntil", {
                        date: grantUntil,
                      })
                    : t("profile.sections.plusGrantActiveBody")}
                </Text>
              </View>
            </View>
          ) : (
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={
                hasSubscription
                  ? t("profile.sections.managePlanA11y")
                  : t("profile.sections.upgradePlanA11y")
              }
              onPress={() =>
                router.push(hasSubscription ? "/subscription" : "/pricing")
              }
              style={({ pressed }) => [
                styles.accountRow,
                pressed && styles.accountRowPressed,
              ]}
            >
              <View style={styles.accountIcon}>
                <Ionicons
                  name="sparkles-outline"
                  size={20}
                  color={colors.primary}
                />
              </View>

              <View style={styles.accountRowCopy}>
                <Text style={styles.accountRowTitle}>
                  {hasSubscription
                    ? t("profile.sections.manageSubscription")
                    : trialOffer
                      ? t("billing.trial.gateCta", { days: trialOffer.days })
                      : t("profile.sections.upgradePlan")}
                </Text>

                <Text style={styles.accountRowDescription}>
                  {hasSubscription
                    ? t("profile.sections.manageSubscriptionBody")
                    : t("profile.sections.upgradePlanBody")}
                </Text>
              </View>

              <Ionicons
                name="chevron-forward"
                size={20}
                color={colors.textMuted}
              />
            </Pressable>
          )}

          <View style={styles.accountDivider} />

          <Pressable
            accessibilityRole="button"
            accessibilityLabel={t("profile.sections.notificationsA11y")}
            onPress={() => setNotificationSettingsOpen(true)}
            style={({ pressed }) => [
              styles.accountRow,
              pressed && styles.accountRowPressed,
            ]}
          >
            <View style={styles.accountIcon}>
              <Ionicons
                name={
                  notificationsEnabled
                    ? "notifications-outline"
                    : "notifications-off-outline"
                }
                size={20}
                color={colors.primary}
              />
            </View>

            <View style={styles.accountRowCopy}>
              <Text style={styles.accountRowTitle}>
                {t("profile.sections.notifications")}
              </Text>

              <Text style={styles.accountRowDescription}>
                {notificationsEnabled
                  ? t("profile.sections.alertsBody")
                  : t("profile.sections.mutedBody")}
              </Text>
            </View>

            <Ionicons
              name="chevron-forward"
              size={20}
              color={colors.textMuted}
            />
          </Pressable>

          <View style={styles.accountDivider} />

          <Pressable
            accessibilityRole="button"
            accessibilityLabel={t("referral.title")}
            onPress={() => router.push("/invite")}
            style={({ pressed }) => [
              styles.accountRow,
              pressed && styles.accountRowPressed,
            ]}
          >
            <View style={styles.accountIcon}>
              <Ionicons name="gift-outline" size={20} color={colors.primary} />
            </View>

            <View style={styles.accountRowCopy}>
              <Text style={styles.accountRowTitle}>{t("referral.title")}</Text>

              <Text style={styles.accountRowDescription}>
                {t("referral.subtitle")}
              </Text>
            </View>

            <Ionicons
              name="chevron-forward"
              size={20}
              color={colors.textMuted}
            />
          </Pressable>

          <View style={styles.accountDivider} />

          <Pressable
            accessibilityRole="button"
            accessibilityLabel={t("settings.language")}
            onPress={() => router.push("/settings/language")}
            style={({ pressed }) => [
              styles.accountRow,
              pressed && styles.accountRowPressed,
            ]}
          >
            <View style={styles.accountIcon}>
              <Ionicons name="globe-outline" size={20} color={colors.primary} />
            </View>

            <View style={styles.accountRowCopy}>
              <Text style={styles.accountRowTitle}>
                {t("settings.language")}
              </Text>

              <Text style={styles.accountRowDescription}>
                {t("settings.languageDescription")}
              </Text>
            </View>

            <Ionicons
              name="chevron-forward"
              size={20}
              color={colors.textMuted}
            />
          </Pressable>

          <View style={styles.accountDivider} />

          <Pressable
            accessibilityRole="button"
            accessibilityLabel={t("profile.sections.helpA11y")}
            onPress={() => router.push("/help")}
            style={({ pressed }) => [
              styles.accountRow,
              pressed && styles.accountRowPressed,
            ]}
          >
            <View style={styles.accountIcon}>
              <Ionicons
                name="help-buoy-outline"
                size={20}
                color={colors.primary}
              />
            </View>

            <View style={styles.accountRowCopy}>
              <Text style={styles.accountRowTitle}>
                {t("profile.sections.help")}
              </Text>

              <Text style={styles.accountRowDescription}>
                {t("profile.sections.helpBody")}
              </Text>
            </View>

            <Ionicons
              name="chevron-forward"
              size={20}
              color={colors.textMuted}
            />
          </Pressable>

          <View style={styles.accountDivider} />

          {/*
            The catch-all route to a block. When a conversation exists, Unblock
            is on the thread itself where the reopen affordance sits — but
            blocking is also reachable from declining a Cue, which creates no
            conversation, so those blocks need somewhere to live too.
          */}
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={t("settings.blocked")}
            onPress={() => router.push("/settings/blocked")}
            style={({ pressed }) => [
              styles.accountRow,
              pressed && styles.accountRowPressed,
            ]}
          >
            <View style={styles.accountIcon}>
              <Ionicons name="ban-outline" size={20} color={colors.primary} />
            </View>

            <View style={styles.accountRowCopy}>
              <Text style={styles.accountRowTitle}>{t("settings.blocked")}</Text>

              <Text style={styles.accountRowDescription}>
                {t("settings.blockedDescription")}
              </Text>
            </View>

            <Ionicons
              name="chevron-forward"
              size={20}
              color={colors.textMuted}
            />
          </Pressable>

          <View style={styles.accountDivider} />

          <Pressable
            accessibilityRole="button"
            accessibilityLabel={t("profile.sections.signOut")}
            onPress={openSignOutConfirmation}
            style={({ pressed }) => [
              styles.accountRow,
              pressed && styles.accountRowPressed,
            ]}
          >
            {/*
              Not a danger action: signing out takes nothing away and you can
              come straight back. Red is reserved for Delete, which is the only
              row here that destroys anything — spending it on this one made the
              two look equally final.
            */}
            <View style={styles.accountIcon}>
              <Ionicons
                name="log-out-outline"
                size={20}
                color={colors.primary}
              />
            </View>

            <View style={styles.accountRowCopy}>
              <Text style={styles.accountRowTitle}>
                {t("profile.sections.signOut")}
              </Text>

              <Text style={styles.accountRowDescription}>
                {t("profile.sections.signOutBody")}
              </Text>
            </View>

            <Ionicons
              name="chevron-forward"
              size={20}
              color={colors.textMuted}
            />
          </Pressable>

          <View style={styles.accountDivider} />

          {/*
            Deliberately above Delete, and deliberately not styled as a danger
            action. Someone who has had enough should meet the reversible option
            first — the destructive one is still there, one row down, for anyone
            who actually wants it.
          */}
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={t(
              deactivated
                ? "profile.sections.reactivate"
                : "profile.sections.deactivate",
            )}
            accessibilityState={{ disabled: changingDeactivation }}
            accessibilityHint={t(
              deactivated
                ? "profile.sections.reactivateHint"
                : "profile.sections.deactivateHint",
            )}
            disabled={changingDeactivation}
            onPress={openDeactivationConfirmation}
            style={({ pressed }) => [
              styles.accountRow,
              changingDeactivation && styles.accountRowDisabled,
              pressed && !changingDeactivation && styles.accountRowPressed,
            ]}
          >
            <View style={styles.accountIcon}>
              <Ionicons
                name={deactivated ? "play-circle-outline" : "pause-circle-outline"}
                size={20}
                color={colors.primary}
              />
            </View>

            <View style={styles.accountRowCopy}>
              <Text style={styles.accountRowTitle}>
                {t(
                  deactivated
                    ? "profile.sections.reactivate"
                    : "profile.sections.deactivate",
                )}
              </Text>

              <Text style={styles.accountRowDescription}>
                {t(
                  deactivated
                    ? "profile.sections.reactivateBody"
                    : "profile.sections.deactivateBody",
                )}
              </Text>
            </View>

            <Ionicons
              name="chevron-forward"
              size={20}
              color={colors.textMuted}
            />
          </Pressable>

          <View style={styles.accountDivider} />

          <Pressable
            accessibilityRole="button"
            accessibilityLabel={t("profile.sections.delete")}
            accessibilityState={{
              disabled: deletingAccount,
            }}
            accessibilityHint={t("profile.sections.deleteHint")}
            disabled={deletingAccount}
            onPress={openDeleteAccountConfirmation}
            style={({ pressed }) => [
              styles.accountRow,
              deletingAccount && styles.accountRowDisabled,
              pressed && !deletingAccount && styles.deleteAccountRowPressed,
            ]}
          >
            <View style={styles.deleteAccountIcon}>
              <Ionicons
                name="trash-outline"
                size={20}
                color={colors.dangerStrong}
              />
            </View>

            <View style={styles.accountRowCopy}>
              <Text style={styles.deleteAccountTitle}>
                {t("profile.sections.delete")}
              </Text>

              <Text style={styles.accountRowDescription}>
                {t("profile.sections.deleteBody")}
              </Text>
            </View>

            <Ionicons
              name="chevron-forward"
              size={20}
              color={colors.dangerStrong}
            />
          </Pressable>

          <View style={styles.accountDivider} />

          <View style={styles.themeAccountRow}>
            <View style={styles.themeAccountIcon}>
              <Ionicons
                name="color-palette-outline"
                size={17}
                color={colors.primary}
              />
            </View>

            <View style={styles.themeAccountContent}>
              <Text style={styles.themeAccountTitle}>
                {t("profile.sections.appearance")}
              </Text>

              <CompactThemeSelector
                value={themePreference}
                onChange={onChangeTheme}
                styles={styles}
                colors={colors}
              />
            </View>
          </View>

          <StayVisibleDiagnosticsRow />

          {__DEV__ ? (
            <>
              <View style={styles.accountDivider} />

              <NotificationToggle
                icon="sparkles-outline"
                label={t("profile.sections.plusDev")}
                description={t("profile.sections.plusDevBody")}
                // Reads and writes the PAID plan, which is what `setDevPlan`
                // sets. Showing the effective tier here would sit ON for a
                // granted account and make toggling it look broken.
                value={hasSubscription}
                disabled={false}
                onPress={() => void setDevPlan(hasSubscription ? "free" : "plus")
                }
                styles={styles}
                colors={colors}
              />
            </>
          ) : null}
        </View>

        <Text style={styles.footerText}>{t("profile.sections.tagline")}</Text>
      </ScrollView>

      <ConfirmationModal
        visible={confirmationType === "signOut"}
        icon="log-out-outline"
        title={t("profile.confirm.signOutTitle")}
        description={t("profile.confirm.signOutBody")}
        confirmLabel={t("profile.sections.signOut")}
        loading={signingOut}
        onCancel={closeConfirmation}
        onConfirm={handleSignOut}
        styles={styles}
        colors={colors}
      />

      <NotificationSettingsModal
        visible={notificationSettingsOpen}
        preferences={notificationPreferences}
        permissionDenied={notificationPermission?.status === "denied"}
        onEnablePush={enableSystemNotifications}
        saving={savingNotifications}
        onChange={updateNotificationPreferences}
        onClose={() => setNotificationSettingsOpen(false)}
        styles={styles}
        colors={colors}
      />

      <ConfirmationModal
        visible={confirmationType === "deactivateAccount"}
        icon="pause-circle-outline"
        title={t("profile.confirm.deactivateTitle")}
        description={t("profile.confirm.deactivateBody")}
        confirmLabel={t("profile.confirm.deactivateAction")}
        loading={changingDeactivation}
        loadingLabel={t("profile.screen.deactivating")}
        onCancel={closeConfirmation}
        onConfirm={handleDeactivationChange}
        styles={styles}
        colors={colors}
      />

      <ConfirmationModal
        visible={confirmationType === "reactivateAccount"}
        icon="play-circle-outline"
        title={t("profile.confirm.reactivateTitle")}
        description={t("profile.confirm.reactivateBody")}
        confirmLabel={t("profile.sections.reactivate")}
        loading={changingDeactivation}
        loadingLabel={t("profile.screen.reactivating")}
        onCancel={closeConfirmation}
        onConfirm={handleDeactivationChange}
        styles={styles}
        colors={colors}
      />

      <ConfirmationModal
        visible={confirmationType === "deleteAccount"}
        icon="trash-outline"
        title={t("profile.confirm.deleteTitle")}
        description={t("profile.confirm.deleteBody")}
        confirmLabel={t("profile.confirm.deleteAction")}
        danger
        loading={deletingAccount}
        loadingLabel={t("profile.screen.deleting")}
        onCancel={closeConfirmation}
        onConfirm={handleDeleteAccount}
        styles={styles}
        colors={colors}
      />
    </SafeAreaView>
  );
}

function CompactThemeSelector({
  value,
  onChange,
  styles,
  colors,
}: {
  value: ThemePreference;
  onChange: (theme: ThemePreference) => void;
  styles: ProfileStyles;
  colors: ThemeColors;
}) {
  const { t } = useTranslation();
  return (
    <View accessibilityRole="radiogroup" style={styles.compactThemeSelector}>
      {THEME_OPTIONS.map((option) => {
        const selected = option.value === value;

        return (
          <Pressable
            key={option.value}
            accessibilityRole="radio"
            accessibilityLabel={t("profile.appearance.themeA11y", {
              theme: t(option.labelKey),
            })}
            accessibilityState={{
              selected,
            }}
            onPress={() => onChange(option.value)}
            style={({ pressed }) => [
              styles.compactThemeOption,
              selected && styles.compactThemeOptionSelected,
              pressed && styles.compactThemeOptionPressed,
            ]}
          >
            <Ionicons
              name={option.icon}
              size={13}
              color={selected ? colors.white : colors.textSecondary}
            />

            <Text
              style={[
                styles.compactThemeText,
                selected && styles.compactThemeTextSelected,
              ]}
            >
              {t(option.labelKey)}
            </Text>
          </Pressable>
        );
      })}
    </View>
  );
}

function SectionHeader({
  icon,
  title,
  description,
  expanded,
  onToggle,
  styles,
  colors,
}: {
  icon: IoniconName;
  title: string;
  description: string;
  expanded: boolean;
  onToggle: () => void;
  styles: ProfileStyles;
  colors: ThemeColors;
}) {
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={title}
      accessibilityState={{ expanded }}
      onPress={onToggle}
      style={({ pressed }) => [
        styles.sectionHeader,
        pressed && styles.sectionHeaderPressed,
      ]}
    >
      <View style={styles.sectionIcon}>
        <Ionicons name={icon} size={19} color={colors.primary} />
      </View>

      <View style={styles.sectionHeadingCopy}>
        <Text style={styles.sectionTitle}>{title}</Text>

        <Text style={styles.sectionDescription}>{description}</Text>
      </View>

      <Ionicons
        name={expanded ? "chevron-down" : "chevron-forward"}
        size={20}
        color={colors.textMuted}
      />
    </Pressable>
  );
}

function DetailRow({
  icon,
  label,
  value,
  styles,
  colors,
  lines = 2,
}: {
  icon: IoniconName;
  label: string;
  value?: string;
  styles: ProfileStyles;
  colors: ThemeColors;
  lines?: number;
}) {
  const { t } = useTranslation();
  const hasValue = Boolean(value);

  return (
    <View style={styles.detailRow}>
      <View style={styles.detailIcon}>
        <Ionicons name={icon} size={18} color={colors.iconNeutral} />
      </View>

      <View style={styles.detailCopy}>
        <Text style={styles.detailLabel}>{label}</Text>

        <Text
          numberOfLines={lines}
          style={[styles.detailValue, !hasValue && styles.detailValueEmpty]}
        >
          {value || t("profile.a11y.notAdded")}
        </Text>
      </View>
    </View>
  );
}

function NotificationSettingsModal({
  visible,
  preferences,
  permissionDenied,
  onEnablePush,
  saving,
  onChange,
  onClose,
  styles,
  colors,
}: {
  visible: boolean;
  preferences: NotificationPreferences;
  /** The OS permission is missing — no push arrives whatever the switches say. */
  permissionDenied: boolean;
  onEnablePush: () => Promise<void>;
  saving: boolean;
  onChange: (patch: Partial<NotificationPreferences>) => Promise<void>;
  onClose: () => void;
  styles: ProfileStyles;
  colors: ThemeColors;
}) {
  const { t } = useTranslation();

  // What the switches SHOW is the effective state. With the OS permission
  // missing, nothing is delivered whatever the stored preference says —
  // rendering the preference painted six switches "on" for a user the OS had
  // muted, with no affordance anywhere in the app to turn the permission back
  // on. Off is the truth, and any tap becomes the recovery path: re-ask the
  // OS, or open its settings page once asking is spent.
  const shown: NotificationPreferences = permissionDenied
    ? {
        muteAll: false,
        newPeople: false,
        cues: false,
        messages: false,
        goalReminders: false,
      }
    : preferences;
  const press = (patch: Partial<NotificationPreferences>) => {
    if (permissionDenied) {
      void onEnablePush();
      return;
    }
    void onChange(patch);
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      statusBarTranslucent
      onRequestClose={onClose}
    >
      <View style={styles.modalScreen} accessibilityViewIsModal>
        <BlurredBackdrop
          accessibilityLabel={t("profile.notifications.closeA11y")}
          onPress={onClose}
          style={styles.modalBackdrop}
        />

        <View style={styles.notificationModal}>
          <View style={styles.notificationModalHeader}>
            <View style={styles.notificationModalIcon}>
              <Ionicons
                name="notifications-outline"
                size={24}
                color={colors.primary}
              />
            </View>

            <View style={styles.notificationModalCopy}>
              <Text style={styles.modalTitle}>
                {t("profile.sections.notifications")}
              </Text>
            </View>

            <Pressable
              accessibilityRole="button"
              accessibilityLabel={t("common.close")}
              onPress={onClose}
              hitSlop={10}
              style={styles.notificationCloseButton}
            >
              <Ionicons name="close" size={20} color={colors.textSecondary} />
            </Pressable>
          </View>

          {permissionDenied ? (
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={t("profile.notifications.systemOff")}
              onPress={() => void onEnablePush()}
              style={styles.notificationPermissionHint}
            >
              <Ionicons
                name="alert-circle-outline"
                size={16}
                color={colors.textSecondary}
              />
              <Text style={styles.notificationPermissionHintText}>
                {t("profile.notifications.systemOff")}
              </Text>
            </Pressable>
          ) : null}

          <View style={styles.notificationList}>
            <NotificationToggle
              icon="notifications-off-outline"
              label={t("profile.notifications.muteAll")}
              description={t("profile.notifications.muteAllBody")}
              value={shown.muteAll}
              disabled={saving}
              onPress={() =>
                press({
                  muteAll: !preferences.muteAll,
                })
              }
              styles={styles}
              colors={colors}
            />


            <View style={styles.notificationDivider} />

            <NotificationToggle
              icon="people-outline"
              label={t("profile.notifications.newPeople")}
              description={t("profile.notifications.newPeopleBody")}
              value={!shown.muteAll && shown.newPeople}
              disabled={saving || shown.muteAll}
              onPress={() =>
                press({
                  newPeople: !preferences.newPeople,
                })
              }
              styles={styles}
              colors={colors}
            />

            <View style={styles.notificationDivider} />

            <NotificationToggle
              icon="chatbubbles-outline"
              label={t("profile.notifications.cues")}
              description={t("profile.notifications.cuesBody")}
              value={!shown.muteAll && shown.cues}
              disabled={saving || shown.muteAll}
              onPress={() =>
                press({
                  cues: !preferences.cues,
                })
              }
              styles={styles}
              colors={colors}
            />

            <View style={styles.notificationDivider} />

            <NotificationToggle
              icon="mail-outline"
              label={t("profile.notifications.messages")}
              description={t("profile.notifications.messagesBody")}
              value={!shown.muteAll && shown.messages}
              disabled={saving || shown.muteAll}
              onPress={() =>
                press({
                  messages: !preferences.messages,
                })
              }
              styles={styles}
              colors={colors}
            />

            <View style={styles.notificationDivider} />

            <NotificationToggle
              icon="flag-outline"
              label={t("profile.notifications.goalReminders")}
              description={t("profile.notifications.goalRemindersBody")}
              value={!shown.muteAll && shown.goalReminders}
              disabled={saving || shown.muteAll}
              onPress={() =>
                press({
                  goalReminders: !preferences.goalReminders,
                })
              }
              styles={styles}
              colors={colors}
            />
          </View>
        </View>
      </View>
    </Modal>
  );
}

function NotificationToggle({
  icon,
  label,
  description,
  value,
  disabled,
  onPress,
  styles,
  colors,
}: {
  icon: IoniconName;
  label: string;
  description: string;
  value: boolean;
  disabled: boolean;
  onPress: () => void;
  styles: ProfileStyles;
  colors: ThemeColors;
}) {
  return (
    <Pressable
      accessibilityRole="switch"
      accessibilityLabel={label}
      accessibilityState={{
        checked: value,
        disabled,
      }}
      disabled={disabled}
      onPress={onPress}
      style={({ pressed }) => [
        styles.notificationRow,
        disabled && styles.notificationRowDisabled,
        pressed && !disabled && styles.accountRowPressed,
      ]}
    >
      <View style={styles.notificationIcon}>
        <Ionicons name={icon} size={19} color={colors.iconNeutral} />
      </View>

      <View style={styles.notificationCopy}>
        <Text style={styles.notificationTitle}>{label}</Text>

        <Text style={styles.notificationDescription}>{description}</Text>
      </View>

      <View
        style={[
          styles.notificationSwitch,
          value && styles.notificationSwitchOn,
        ]}
      >
        <View
          style={[
            styles.notificationSwitchThumb,
            value && styles.notificationSwitchThumbOn,
          ]}
        />
      </View>
    </Pressable>
  );
}

function ConfirmationModal({
  visible,
  icon,
  title,
  description,
  confirmLabel,
  danger = false,
  loading = false,
  loadingLabel,
  onCancel,
  onConfirm,
  styles,
  colors,
}: {
  visible: boolean;
  icon: IoniconName;
  title: string;
  description: string;
  confirmLabel: string;
  danger?: boolean;
  loading?: boolean;
  loadingLabel?: string;
  onCancel: () => void;
  onConfirm: () => void;
  styles: ProfileStyles;
  colors: ThemeColors;
}) {
  const { t } = useTranslation();
  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      statusBarTranslucent
      onRequestClose={() => {
        if (!loading) onCancel();
      }}
    >
      <View style={styles.modalScreen} accessibilityViewIsModal>
        <BlurredBackdrop
          accessibilityLabel={t("profile.confirm.closeA11y")}
          onPress={() => {
            if (!loading) onCancel();
          }}
          style={styles.modalBackdrop}
        />

        <View style={styles.confirmationModal}>
          <View style={[styles.modalIcon, danger && styles.deleteModalIcon]}>
            <Ionicons
              name={icon}
              size={26}
              color={danger ? colors.dangerStrong : colors.danger}
            />
          </View>

          <Text style={styles.modalTitle}>{title}</Text>

          <Text style={styles.modalDescription}>{description}</Text>

          {danger ? (
            <View style={styles.warningBox}>
              <Ionicons
                name="alert-circle-outline"
                size={18}
                color={colors.dangerStrong}
              />
              <Text style={styles.warningText}>
                {t("profile.confirm.deletePermanent")}
              </Text>
            </View>
          ) : null}

          <View style={styles.modalActions}>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={t("common.cancel")}
              accessibilityState={{
                disabled: loading,
              }}
              disabled={loading}
              onPress={onCancel}
              style={({ pressed }) => [
                styles.modalButton,
                styles.cancelButton,
                loading && styles.modalButtonDisabled,
                pressed && !loading && styles.modalButtonPressed,
              ]}
            >
              <Text style={styles.cancelButtonText}>{t("common.cancel")}</Text>
            </Pressable>

            <Pressable
              accessibilityRole="button"
              accessibilityLabel={confirmLabel}
              accessibilityState={{
                busy: loading,
                disabled: loading,
              }}
              disabled={loading}
              onPress={onConfirm}
              style={({ pressed }) => [
                styles.modalButton,
                danger
                  ? styles.deleteConfirmButton
                  : styles.signOutConfirmButton,
                loading && styles.modalButtonDisabled,
                pressed && !loading && styles.modalButtonPressed,
              ]}
            >
              {loading ? (
                <ActivityIndicator size="small" color={colors.white} />
              ) : (
                <Ionicons name={icon} size={17} color={colors.white} />
              )}

              <Text style={styles.confirmButtonText}>
                {loading && loadingLabel ? loadingLabel : confirmLabel}
              </Text>
            </Pressable>
          </View>
        </View>
      </View>
    </Modal>
  );
}

/**
 * How far the top-bar plan label sits below the centre of the wordmark row, so
 * that the middle of its cap F lines up with the wordmark's pulse dot.
 *
 * Optical, not computed: the dot is placed by the Logo's own `marginTop:
 * size * 0.27`, and the label's cap height depends on the shipped typeface's
 * metrics, so there is no expression here that would be more honest than a
 * measured number. Positive moves the label DOWN. This is the only value to
 * change if it reads high or low.
 */
const PLAN_LABEL_NUDGE = 3;

function createStyles(colors: ThemeColors, isDark: boolean) {
  return StyleSheet.create({
    safe: {
      flex: 1,
      backgroundColor: colors.screen,
    },

    errorFallback: {
      flex: 1,
      width: "100%",
      maxWidth: 430,
      alignSelf: "center",
      alignItems: "center",
      justifyContent: "center",
      paddingHorizontal: 24,
      gap: 14,
    },

    errorIcon: {
      width: 54,
      height: 54,
      alignItems: "center",
      justifyContent: "center",
      borderRadius: 18,
      borderWidth: StyleSheet.hairlineWidth,
      borderColor: colors.primaryBorder,
      backgroundColor: colors.primarySurface,
    },

    errorTitle: {
      color: colors.textPrimary,
      fontFamily: FONT_FAMILY,
      fontSize: 22,
      lineHeight: 28,
      fontWeight: "800",
      textAlign: "center",
    },

    errorText: {
      color: colors.textBody,
      fontFamily: FONT_FAMILY,
      fontSize: 14,
      lineHeight: 21,
      fontWeight: "600",
      textAlign: "center",
    },

    errorDetails: {
      width: "100%",
      color: colors.textMuted,
      fontFamily: FONT_FAMILY,
      fontSize: 12,
      lineHeight: 17,
      textAlign: "center",
    },

    errorRetryButton: {
      flex: 0,
      minWidth: 160,
      marginTop: 4,
    },

    topBar: {
      position: "relative",
      minHeight: 69,
      paddingHorizontal: 24,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: colors.divider,
      backgroundColor: colors.topBar,
    },

    topBarBrand: {
      flexDirection: "row",
      // Centred, then nudged — see PLAN_LABEL_NUDGE. Neither of the two
      // "correct" answers reads right here: centring the BOXES floats the
      // label, because "opncue" is 24px lowercase whose only descender is the
      // p and so sits low in its line box, while "Free" has a cap F and no
      // descender and sits high in its own; sharing a BASELINE then drops the
      // label below the pulse dot, which is what the eye actually pairs it
      // with. So the dot is the reference, and the offset is explicit.
      alignItems: "center",
      gap: 8,
    },

    topBarTitle: {
      flexShrink: 1,
      color: colors.primary,
      fontFamily: FONT_FAMILY,
      fontSize: 13,
      lineHeight: 18,
      fontWeight: "800",
      letterSpacing: 1.2,
    },

    content: {
      width: "100%",
      maxWidth: 680,
      alignSelf: "center",
      paddingHorizontal: 12,
      paddingTop: Platform.OS === "web" ? 0 : 20,
      paddingBottom: Platform.OS === "web" ? 0 : 16,
      gap: Platform.OS === "web" ? 0 : 16,
    },

    card: {
      overflow: "hidden",
      borderRadius: 20,
      borderWidth: StyleSheet.hairlineWidth,
      borderColor: colors.borderSoft,
      backgroundColor: colors.surface,
      shadowColor: colors.shadow,
      shadowOpacity: isDark ? 0.24 : 0.07,
      shadowRadius: 15,
      shadowOffset: {
        width: 0,
        height: 7,
      },
      elevation: 3,
    },

    profileGroup: {
      padding: 16,
      gap: 20,
    },

    profileDescription: {
      maxWidth: 410,
      color: colors.textSecondary,
      fontFamily: FONT_FAMILY,
      fontSize: 13,
      lineHeight: 20,
    },

    profileSummary: {
      flexDirection: "row",
      alignItems: "center",
      gap: 17,
    },

    avatarSection: {
      position: "relative",
    },

    avatarBorder: {
      padding: 4,
      borderRadius: 52,
      borderWidth: 1,
      borderColor: colors.primaryBorder,
      backgroundColor: colors.surfaceSoft,
    },

    onlineDot: {
      position: "absolute",
      right: 5,
      bottom: 5,
      width: 17,
      height: 17,
      borderRadius: 9,
      borderWidth: 3,
      borderColor: colors.surface,
      // Blue, matching the inbox row and the chat header: every presence
      // dot in the app moved off green together, and this one was missed.
      backgroundColor: colors.primary,
    },

    identity: {
      flex: 1,
      alignItems: "flex-start",
    },

    displayName: {
      flexShrink: 1,
      color: colors.textPrimary,
      fontFamily: FONT_FAMILY,
      fontSize: 23,
      lineHeight: 29,
      fontWeight: "700",
      letterSpacing: 0,
    },

    profileHeadline: {
      marginTop: 4,
      color: colors.textSecondary,
      fontFamily: FONT_FAMILY,
      fontSize: 13,
      lineHeight: 18,
      fontWeight: "600",
    },

    vibeRow: {
      minHeight: 31,
      marginTop: 7,
      paddingHorizontal: 10,
      flexDirection: "row",
      alignItems: "center",
      gap: 6,
      borderRadius: 9,
      backgroundColor: colors.surfaceMuted,
    },

    vibeEmoji: {
      fontSize: 15,
    },

    vibeText: {
      color: colors.textBody,
      fontFamily: FONT_FAMILY,
      fontSize: 12,
      fontWeight: "600",
    },

    divider: {
      height: StyleSheet.hairlineWidth,
      backgroundColor: colors.divider,
    },




    section: {
      gap: 13,
    },

    sectionHeader: {
      flex: 1,
      flexDirection: "row",
      alignItems: "center",
      gap: 11,
    },

    sectionHeaderPressed: {
      opacity: 0.6,
    },

    sectionIcon: {
      width: 40,
      height: 40,
      borderRadius: 10,
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: colors.primarySoft,
    },

    sectionHeadingCopy: {
      flex: 1,
    },

    sectionTitle: {
      color: colors.textStrong,
      fontFamily: FONT_FAMILY,
      fontSize: 15,
      lineHeight: 20,
      fontWeight: "700",
    },

    sectionDescription: {
      marginTop: 2,
      color: colors.textSecondary,
      fontFamily: FONT_FAMILY,
      fontSize: 11,
      lineHeight: 16,
    },

    aboutContent: {
      minHeight: 80,
      paddingHorizontal: 14,
      paddingVertical: 13,
      borderRadius: 11,
      borderWidth: 1,
      borderColor: colors.borderSoft,
      backgroundColor: colors.surfaceMuted,
    },

    aboutText: {
      color: colors.textBody,
      fontFamily: FONT_FAMILY,
      fontSize: 13,
      lineHeight: 21,
    },

    aboutHeadline: {
      marginBottom: 7,
      color: colors.textStrong,
      fontFamily: FONT_FAMILY,
      fontSize: 13.5,
      lineHeight: 19,
      fontWeight: "700",
    },

    aboutPlaceholder: {
      color: colors.textMuted,
      fontStyle: "italic",
    },

    detailsHeading: {
      flexDirection: "row",
      alignItems: "center",
      gap: 10,
    },

    revealButton: {
      minHeight: 34,
      paddingHorizontal: 11,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      gap: 5,
      borderRadius: 9,
      borderWidth: 1,
      borderColor: colors.primaryBorder,
      backgroundColor: colors.primarySurface,
    },

    hideButton: {
      borderColor: colors.border,
      backgroundColor: colors.surfaceMuted,
    },

    revealButtonPressed: {
      opacity: 0.72,
    },

    revealButtonText: {
      color: colors.primaryStrong,
      fontFamily: FONT_FAMILY,
      fontSize: 11,
      fontWeight: "700",
    },

    hideButtonText: {
      color: colors.textSecondary,
    },

    detailsStatus: {
      minHeight: 38,
      paddingHorizontal: 12,
      flexDirection: "row",
      alignItems: "center",
      gap: 8,
      borderRadius: 10,
      backgroundColor: colors.surfaceMuted,
    },

    statusDot: {
      width: 8,
      height: 8,
      borderRadius: 4,
    },

    visibleDot: {
      backgroundColor: colors.green,
    },

    hiddenDot: {
      backgroundColor: colors.hidden,
    },

    detailsStatusText: {
      flex: 1,
      color: colors.textSecondary,
      fontFamily: FONT_FAMILY,
      fontSize: 10,
      lineHeight: 15,
    },

    detailsList: {
      overflow: "hidden",
      borderRadius: 12,
      borderWidth: StyleSheet.hairlineWidth,
      borderColor: colors.divider,
      backgroundColor: colors.surfaceMuted,
    },

    detailRow: {
      minHeight: 72,
      paddingHorizontal: 13,
      paddingVertical: 11,
      flexDirection: "row",
      alignItems: "center",
      gap: 11,
    },

    detailIcon: {
      width: 38,
      height: 38,
      borderRadius: 10,
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: colors.primarySoft,
    },

    detailCopy: {
      flex: 1,
      gap: 2,
    },

    detailLabel: {
      color: colors.textSecondary,
      fontFamily: FONT_FAMILY,
      fontSize: 10,
      lineHeight: 14,
      fontWeight: "700",
      textTransform: "uppercase",
      letterSpacing: 0.5,
    },

    detailValue: {
      color: colors.textStrong,
      fontFamily: FONT_FAMILY,
      fontSize: 13,
      lineHeight: 18,
      fontWeight: "600",
    },

    detailValueEmpty: {
      color: colors.textMuted,
      fontWeight: "500",
    },

    detailDivider: {
      height: StyleSheet.hairlineWidth,
      marginLeft: 62,
      backgroundColor: colors.divider,
    },

    privacyNote: {
      paddingHorizontal: 2,
      flexDirection: "row",
      alignItems: "flex-start",
      gap: 7,
    },

    privacyNoteText: {
      flex: 1,
      color: colors.textSecondary,
      fontFamily: FONT_FAMILY,
      fontSize: 10,
      lineHeight: 15,
    },

    planText: {
      marginTop: PLAN_LABEL_NUDGE,
      color: colors.primaryText,
      fontFamily: FONT_FAMILY,
      // 13 is the top bar's own size (see topBarTitle). 11 was chosen for a
      // pill next to a 23px name; alone beside a 24px wordmark it read small.
      fontSize: 13,
      lineHeight: 18,
      fontWeight: "800",
    },

    cardTitle: {
      paddingHorizontal: 17,
      paddingTop: 17,
      paddingBottom: 8,
      color: colors.textPrimary,
      fontFamily: FONT_FAMILY,
      fontSize: 18,
      lineHeight: 23,
      fontWeight: "700",
    },

    groupDivider: {
      height: StyleSheet.hairlineWidth,
      marginHorizontal: 16,
      backgroundColor: colors.divider,
    },

    // Sits inside profileGroup (16px padding); the extra left margin lines the
    // divider up with the row text (icon 40 + gap 11), matching accountDivider.
    sectionDivider: {
      height: StyleSheet.hairlineWidth,
      marginLeft: 51,
      backgroundColor: colors.divider,
    },

    accountRow: {
      minHeight: 76,
      paddingHorizontal: 16,
      paddingVertical: 13,
      flexDirection: "row",
      alignItems: "center",
      gap: 12,
    },

    accountRowPressed: {
      backgroundColor: colors.surfacePressed,
    },

    accountRowDisabled: {
      opacity: 0.55,
    },

    deleteAccountRowPressed: {
      backgroundColor: colors.dangerSoftStrong,
    },

    accountIcon: {
      width: 42,
      height: 42,
      borderRadius: 10,
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: colors.primarySoft,
    },

    accountRowCopy: {
      flex: 1,
    },

    accountRowTitle: {
      color: colors.textStrong,
      fontFamily: FONT_FAMILY,
      fontSize: 14,
      lineHeight: 19,
      fontWeight: "700",
    },

    accountRowDescription: {
      marginTop: 2,
      color: colors.textSecondary,
      fontFamily: FONT_FAMILY,
      fontSize: 11,
      lineHeight: 16,
    },

    accountDivider: {
      height: StyleSheet.hairlineWidth,
      marginLeft: 70,
      marginRight: 16,
      backgroundColor: colors.divider,
    },

    deleteAccountIcon: {
      width: 42,
      height: 42,
      borderRadius: 10,
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: colors.dangerSoftStrong,
    },

    deleteAccountTitle: {
      color: colors.dangerStrong,
      fontFamily: FONT_FAMILY,
      fontSize: 14,
      lineHeight: 19,
      fontWeight: "700",
    },

    themeAccountRow: {
      minHeight: 72,
      paddingHorizontal: 16,
      paddingVertical: 10,
      flexDirection: "row",
      alignItems: "center",
      gap: 10,
    },

    themeAccountIcon: {
      width: 36,
      height: 36,
      borderRadius: 9,
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: colors.primarySoft,
    },

    themeAccountContent: {
      flex: 1,
      gap: 6,
    },

    themeAccountTitle: {
      color: colors.textStrong,
      fontFamily: FONT_FAMILY,
      fontSize: 14,
      lineHeight: 19,
      fontWeight: "700",
    },

    compactThemeSelector: {
      flexDirection: "row",
      gap: 4,
    },

    compactThemeOption: {
      flex: 1,
      minHeight: 29,
      paddingHorizontal: 5,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      gap: 4,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surfaceMuted,
    },

    compactThemeOptionSelected: {
      borderColor: colors.primary,
      backgroundColor: colors.primary,
    },

    compactThemeOptionPressed: {
      opacity: 0.75,
      transform: [{ scale: 0.98 }],
    },

    compactThemeText: {
      color: colors.textSecondary,
      fontFamily: FONT_FAMILY,
      fontSize: 11,
      lineHeight: 15,
      fontWeight: "700",
    },

    compactThemeTextSelected: {
      color: colors.white,
    },

    footerText: {
      marginTop: Platform.OS === "web" ? 20 : 4,
      marginBottom: Platform.OS === "web" ? 20 : 4,
      color: colors.textMuted,
      fontFamily: FONT_FAMILY,
      fontSize: 11,
      textAlign: "center",
    },

    modalScreen: {
      flex: 1,
      paddingHorizontal: 24,
      alignItems: "center",
      justifyContent: "center",
    },

    modalBackdrop: {
      ...StyleSheet.absoluteFill,
      backgroundColor: colors.modalBackdrop,
    },

    notificationModal: {
      width: "100%",
      maxWidth: 390,
      overflow: "hidden",
      borderRadius: 22,
      borderWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surface,
      shadowColor: colors.shadow,
      shadowOpacity: isDark ? 0.38 : 0.18,
      shadowRadius: 26,
      shadowOffset: {
        width: 0,
        height: 14,
      },
      elevation: 14,
    },

    notificationModalHeader: {
      paddingHorizontal: 18,
      paddingTop: 18,
      paddingBottom: 14,
      flexDirection: "row",
      alignItems: "center",
      gap: 12,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: colors.divider,
    },

    notificationModalIcon: {
      width: 46,
      height: 46,
      borderRadius: 14,
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: colors.primarySoft,
    },

    notificationModalCopy: {
      flex: 1,
      gap: 3,
    },

    notificationModalDescription: {
      color: colors.textSecondary,
      fontFamily: FONT_FAMILY,
      fontSize: 12,
      lineHeight: 17,
    },

    notificationCloseButton: {
      width: 36,
      height: 36,
      borderRadius: 11,
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: colors.surfaceMuted,
    },

    notificationList: {
      backgroundColor: colors.surface,
    },

    // The "push is off in system settings" strip above the toggles. Warm
    // rather than red: nothing failed, the user made a choice the app is
    // offering a way back from.
    notificationPermissionHint: {
      flexDirection: "row",
      alignItems: "center",
      gap: 8,
      paddingHorizontal: 16,
      paddingVertical: 10,
      backgroundColor: colors.surfaceMuted,
    },

    notificationPermissionHintText: {
      flex: 1,
      fontSize: 12.5,
      lineHeight: 17,
      color: colors.textSecondary,
    },

    notificationRow: {
      minHeight: 76,
      paddingHorizontal: 16,
      paddingVertical: 12,
      flexDirection: "row",
      alignItems: "center",
      gap: 12,
    },

    notificationRowDisabled: {
      opacity: 0.52,
    },

    notificationIcon: {
      width: 40,
      height: 40,
      borderRadius: 10,
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: colors.surfaceMuted,
    },

    notificationCopy: {
      flex: 1,
      gap: 2,
    },

    notificationTitle: {
      color: colors.textStrong,
      fontFamily: FONT_FAMILY,
      fontSize: 14,
      lineHeight: 19,
      fontWeight: "700",
    },

    notificationDescription: {
      color: colors.textSecondary,
      fontFamily: FONT_FAMILY,
      fontSize: 11,
      lineHeight: 16,
    },

    notificationSwitch: {
      width: 44,
      height: 26,
      padding: 3,
      borderRadius: 13,
      justifyContent: "center",
      backgroundColor: colors.surfaceMuted,
    },

    notificationSwitchOn: {
      backgroundColor: colors.primary,
    },

    notificationSwitchThumb: {
      width: 20,
      height: 20,
      borderRadius: 10,
      backgroundColor: colors.white,
      shadowColor: colors.shadow,
      shadowOpacity: 0.16,
      shadowRadius: 4,
      shadowOffset: {
        width: 0,
        height: 2,
      },
      elevation: 2,
    },

    notificationSwitchThumbOn: {
      transform: [{ translateX: 18 }],
    },

    notificationDivider: {
      height: StyleSheet.hairlineWidth,
      marginLeft: 68,
      backgroundColor: colors.divider,
    },

    planModal: {
      width: "100%",
      maxWidth: 420,
      maxHeight: "88%",
      paddingHorizontal: 18,
      paddingTop: 18,
      paddingBottom: 18,
      borderRadius: 22,
      borderWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surface,
      shadowColor: colors.shadow,
      shadowOpacity: isDark ? 0.38 : 0.18,
      shadowRadius: 26,
      shadowOffset: {
        width: 0,
        height: 14,
      },
      elevation: 14,
    },

    planHero: {
      flexDirection: "row",
      alignItems: "center",
      gap: 12,
    },

    planScroll: {
      maxHeight: 470,
    },

    planPrice: {
      color: colors.primaryText,
      fontFamily: FONT_FAMILY,
      fontSize: 13,
      lineHeight: 18,
      fontWeight: "800",
    },

    planIntro: {
      marginTop: 14,
      color: colors.textSecondary,
      fontFamily: FONT_FAMILY,
      fontSize: 12,
      lineHeight: 18,
    },

    planColumns: {
      marginTop: 16,
      gap: 10,
    },

    planColumn: {
      paddingHorizontal: 13,
      paddingVertical: 12,
      gap: 8,
      borderRadius: 14,
      borderWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surfaceMuted,
    },

    planColumnActive: {
      borderColor: colors.primaryBorder,
      backgroundColor: colors.primarySurface,
    },

    planColumnHeader: {
      minHeight: 25,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 8,
    },

    planColumnTitle: {
      color: colors.textStrong,
      fontFamily: FONT_FAMILY,
      fontSize: 14,
      lineHeight: 19,
      fontWeight: "800",
    },

    currentPlanPill: {
      minHeight: 22,
      paddingHorizontal: 8,
      justifyContent: "center",
      borderRadius: 11,
      backgroundColor: colors.primary,
    },

    currentPlanText: {
      color: colors.white,
      fontFamily: FONT_FAMILY,
      fontSize: 10,
      lineHeight: 13,
      fontWeight: "800",
    },

    planFeatureRow: {
      flexDirection: "row",
      alignItems: "flex-start",
      gap: 7,
    },

    planFeatureText: {
      flex: 1,
      color: colors.textBody,
      fontFamily: FONT_FAMILY,
      fontSize: 11,
      lineHeight: 16,
      fontWeight: "600",
    },

    planSupportBox: {
      marginTop: 14,
      paddingHorizontal: 12,
      paddingVertical: 10,
      flexDirection: "row",
      alignItems: "flex-start",
      gap: 8,
      borderRadius: 12,
      backgroundColor: colors.surfaceMuted,
    },

    planSupportText: {
      flex: 1,
      color: colors.textSecondary,
      fontFamily: FONT_FAMILY,
      fontSize: 11,
      lineHeight: 16,
      fontWeight: "600",
    },

    confirmationModal: {
      width: "100%",
      maxWidth: 370,
      paddingHorizontal: 22,
      paddingTop: 24,
      paddingBottom: 20,
      alignItems: "center",
      borderRadius: 22,
      borderWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surface,
      shadowColor: colors.shadow,
      shadowOpacity: isDark ? 0.38 : 0.18,
      shadowRadius: 26,
      shadowOffset: {
        width: 0,
        height: 14,
      },
      elevation: 14,
    },

    modalIcon: {
      width: 54,
      height: 54,
      marginBottom: 15,
      alignItems: "center",
      justifyContent: "center",
      borderRadius: 17,
      backgroundColor: colors.dangerSoft,
    },

    deleteModalIcon: {
      backgroundColor: colors.dangerSoftStrong,
    },

    modalTitle: {
      color: colors.textStrong,
      fontFamily: FONT_FAMILY,
      fontSize: 20,
      lineHeight: 26,
      fontWeight: "700",
      textAlign: "center",
    },

    modalDescription: {
      maxWidth: 305,
      marginTop: 8,
      color: colors.textSecondary,
      fontFamily: FONT_FAMILY,
      fontSize: 13,
      lineHeight: 20,
      textAlign: "center",
    },

    warningBox: {
      width: "100%",
      marginTop: 17,
      paddingHorizontal: 12,
      paddingVertical: 11,
      flexDirection: "row",
      alignItems: "flex-start",
      gap: 8,
      borderRadius: 11,
      borderWidth: 1,
      borderColor: colors.dangerBorder,
      backgroundColor: colors.dangerSoftStrong,
    },

    warningText: {
      flex: 1,
      color: colors.dangerStrong,
      fontFamily: FONT_FAMILY,
      fontSize: 11,
      lineHeight: 17,
    },

    modalActions: {
      width: "100%",
      marginTop: 22,
      flexDirection: "row",
      gap: 10,
    },

    modalButton: {
      flex: 1,
      minHeight: 48,
      paddingHorizontal: 10,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      gap: 7,
      borderRadius: 13,
    },

    modalButtonPressed: {
      opacity: 0.75,
    },

    modalButtonDisabled: {
      opacity: 0.72,
    },

    cancelButton: {
      borderWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surfaceMuted,
    },

    cancelButtonText: {
      color: colors.textBody,
      fontFamily: FONT_FAMILY,
      fontSize: 14,
      fontWeight: "700",
    },

    signOutConfirmButton: {
      backgroundColor: isDark ? "#B85A66" : "#B34A55",
    },

    planConfirmButton: {
      backgroundColor: colors.primary,
    },

    deleteConfirmButton: {
      backgroundColor: isDark ? "#C64350" : "#B4232F",
    },

    confirmButtonText: {
      color: colors.white,
      fontFamily: FONT_FAMILY,
      fontSize: 14,
      fontWeight: "700",
    },
  });
}

type ProfileStyles = ReturnType<typeof createStyles>;
